<template>
  <div>
    <o-card>
      <template slot="header">
        <h4>{{ $t('sandoz.product_matrix') }}</h4>
      </template>
      <div class="mb-3">
        <b-row>
          <b-col cols="2">
            <label>{{ $t('sandoz.year') }}</label>
            <b-form-select v-model="selectedYear" @change="onYearChange">
              <option v-for="year in years" :key="year.value" :value="year.value">
                {{ year.text }}
              </option>
            </b-form-select>
          </b-col>
          <b-col cols="2">
            <label>{{ $t('sandoz.language') }}</label>
            <b-form-select v-model="selectedLanguage" @change="onLanguageChange">
              <option v-for="lang in languages" :key="lang.value" :value="lang.value">
                {{ lang.text }}
              </option>
            </b-form-select>
          </b-col>
          <b-col cols="8">
            <label>
              {{ $t('sandoz.contract_types') }} (
              <b-form-checkbox
                v-if="availableSalesLines.length > 0"
                v-model="allContractTypesSelected"
                @change="toggleAllContractTypes"
                class="d-inline-block"
                inline
              >
                {{ $t('sandoz.select_all') }}
              </b-form-checkbox>
              )
            </label>
            <div v-if="availableSalesLines.length > 0" class="border p-2" style="max-height: 150px; overflow-y: auto;">
              <b-form-checkbox-group v-model="selectedContractTypes" class="d-flex flex-wrap">
                <b-form-checkbox
                  v-for="salesLine in availableSalesLines"
                  :key="salesLine.id"
                  :value="salesLine.id"
                  class="mr-3 mb-1"
                >
                  {{ salesLine.sales_line }}
                </b-form-checkbox>
              </b-form-checkbox-group>
            </div>
            <div v-else class="text-muted p-2">
              {{ $t('sandoz.select_year_first') }}
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col cols="auto">
            <b-button 
              variant="primary" 
              @click="loadData" 
              :disabled="loading || !canLoad"
            >
              <fa icon="spinner" spin v-if="loading" class="mr-1" />
              <fa icon="search" v-else class="mr-1" />
              {{ $t('core.load') }}
            </b-button>
          </b-col>
          <b-col cols="auto" v-if="productMasters.length > 0">
            <b-button variant="success" @click="saveAll" :disabled="loading || !hasChanges || selectedContractTypes.length === 0">
              <fa icon="save" class="mr-1" />
              {{ $t('core.save') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div v-if="loading && productMasters.length === 0" class="text-center py-5">
        <fa icon="spinner" spin size="2x" />
        <p class="mt-2">{{ $t('sandoz.loading_product_matrix') }}</p>
      </div>

      <div v-else-if="productMasters && productMasters.length > 0 && selectedSalesLines.length > 0 && selectedSalesLines.length === selectedContractTypes.length" class="table-responsive">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th style="min-width: 300px;">{{ $t('sandoz.product') }}</th>
              <th v-for="salesLine in selectedSalesLines" :key="salesLine.id" class="text-center" style="min-width: 150px; max-width: 200px;">
                {{ salesLine.sales_line }}
              </th>
            </tr>
          </thead>
          <tbody>
            <product-matrix-row
              v-for="product in productMasters"
              :key="product.id"
              :product="product"
              :sales-lines="selectedSalesLines"
              :level="0"
              @update="handleUpdate"
            />
          </tbody>
        </table>
      </div>

      <div v-else-if="productMasters && productMasters.length > 0 && selectedContractTypes.length === 0" class="text-center py-5 text-muted">
        {{ $t('sandoz.select_contract_types_to_view') }}
      </div>

      <div v-else-if="productMasters.length === 0 && !loading" class="text-center py-5 text-muted">
        {{ $t('sandoz.click_load_to_view_matrix') }}
      </div>

      <div v-else-if="productMasters.length === 0 && loading" class="text-center py-5">
        <fa icon="spinner" spin size="2x" />
      </div>
    </o-card>
  </div>
</template>

<script>
import axios from 'axios'
import { contractYears } from '~/utils/contractYear'
import ProductMatrixRow from './components/ProductMatrixRow'

export default {
  name: 'ProductMatrix',
  components: {
    ProductMatrixRow
  },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      years: contractYears(),
      selectedLanguage: 'de',
      languages: [
        {value: 'de', text: 'DE'},
        {value: 'en', text: 'EN'},
        {value: 'fr', text: 'FR'},
        {value: 'it', text: 'IT'}
      ],
      loading: false,
      productMasters: [],
      salesLines: [],
      availableSalesLines: [],
      selectedContractTypes: [],
      changes: new Map(),
      hasChanges: false
    }
  },
  computed: {
    selectedSalesLines() {
      // Only return sales lines that were actually loaded in the data
      return this.salesLines.filter(sl => this.selectedContractTypes.includes(sl.id))
    },
    allContractTypesSelected: {
      get() {
        return this.availableSalesLines.length > 0 && this.selectedContractTypes.length === this.availableSalesLines.length
      },
      set(value) {
        // This is handled by toggleAllContractTypes method
      }
    },
    canLoad() {
      return this.selectedYear && this.selectedLanguage && this.availableSalesLines.length > 0 && this.selectedContractTypes.length > 0
    },
    loadedContractTypeIds() {
      // Track which contract types the currently loaded data is for
      return this.salesLines.map(sl => sl.id)
    }
  },
  watch: {
    selectedContractTypes(newVal, oldVal) {
      // If data is loaded and contract types changed, check if we need to clear the data
      if (this.productMasters.length > 0 && this.salesLines.length > 0) {
        // Check if all selected contract types are in the loaded data
        const selectedSet = new Set(newVal)
        const loadedSet = new Set(this.loadedContractTypeIds)
        const allSelectedAreLoaded = [...selectedSet].every(id => loadedSet.has(id))
        
        // If any selected type is not in the loaded data, clear the data
        // This prevents showing columns with no data
        if (!allSelectedAreLoaded || newVal.length === 0) {
          this.productMasters = []
          this.salesLines = []
          this.hasChanges = false
          this.changes.clear()
        }
      }
    }
  },
  mounted() {
    this.loadAvailableSalesLines()
  },
  methods: {
    loadAvailableSalesLines() {
      // Load just the sales lines list without product data
      axios.get('/api/sandoz/sales_lines?per_page=9999&sortBy=sales_line&sortByDirection=asc')
        .then(response => {
          this.availableSalesLines = response.data.data || []
          // Don't select any by default - user must select at least one
        })
        .catch(error => {
          console.error('Error loading sales lines:', error)
        })
    },
    onYearChange() {
      // Clear product data when year changes
      this.productMasters = []
      this.salesLines = []
      this.hasChanges = false
      this.changes.clear()
    },
    onLanguageChange() {
      // Clear product data when language changes
      this.productMasters = []
      this.salesLines = []
      this.hasChanges = false
      this.changes.clear()
    },
    loadData() {
      if (!this.canLoad) {
        return
      }

      // Validate that at least one contract type is selected
      if (!this.selectedContractTypes || this.selectedContractTypes.length === 0) {
        this.$bvToast.toast(this.$t('sandoz.select_at_least_one_contract_type'), {
          title: this.$t('core.error'),
          variant: 'warning',
          solid: true
        })
        return
      }

      this.loading = true
      this.hasChanges = false
      this.changes.clear()

      // Pass selected contract types and language to filter products
      const params = {
        year: this.selectedYear,
        language: this.selectedLanguage,
        sales_line_ids: this.selectedContractTypes.join(',')
      }

      return axios.get('/api/sandoz/product_matrix', { params })
        .then(response => {
          this.productMasters = response.data.product_masters
          this.salesLines = response.data.sales_lines
          
          // Ensure selected contract types match what's available
          const availableIds = this.salesLines.map(sl => sl.id)
          this.selectedContractTypes = this.selectedContractTypes.filter(id => availableIds.includes(id))
        })
        .catch(error => {
          console.error('Error loading product matrix:', error)
          this.$bvToast.toast(this.$t('core.error_loading_data'), {
            title: this.$t('core.error'),
            variant: 'danger',
            solid: true
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    toggleAllContractTypes() {
      if (this.allContractTypesSelected) {
        this.selectedContractTypes = []
      } else {
        this.selectedContractTypes = this.availableSalesLines.map(sl => sl.id)
      }
    },
    handleUpdate(productMasterId, salesLineId, data) {
      const key = `${productMasterId}_${salesLineId}`
      if (data) {
        this.changes.set(key, {
          product_master_id: productMasterId,
          sales_line_id: salesLineId,
          ...data
        })
      } else {
        this.changes.delete(key)
      }
      this.hasChanges = this.changes.size > 0
    },
    saveAll() {
      if (this.changes.size === 0) {
        return
      }

      this.loading = true
      const updates = Array.from(this.changes.values())

      // Create promises for all updates
      const promises = updates.map(update => {
        const { sales_line_id, ...updateData } = update
        if (update.id) {
          // Update existing
          return axios.post(`/api/sandoz/product_master_sales_lines/${update.id}`, updateData)
        } else {
          // Create new - need to find if record exists first
          return axios.get(`/api/sandoz/product_master_sales_lines?filter[product_master_id]=${update.product_master_id}&filter[sales_line_id]=${sales_line_id}&per_page=1`)
            .then(response => {
              if (response.data.data && response.data.data.length > 0) {
                // Update existing record
                const existingId = response.data.data[0].id
                return axios.post(`/api/sandoz/product_master_sales_lines/${existingId}`, updateData)
              } else {
                // Create new record
                return axios.post('/api/sandoz/product_master_sales_lines/new', updateData)
              }
            })
        }
      })

      Promise.all(promises)
        .then(() => {
          this.$bvToast.toast(this.$t('core.data_saved'), {
            title: this.$t('core.success'),
            variant: 'success',
            solid: true
          })
          const selectedTypes = [...this.selectedContractTypes]
          this.changes.clear()
          this.hasChanges = false
          // Reload data to get updated values
          const params = {
            year: this.selectedYear,
            language: this.selectedLanguage,
            sales_line_ids: selectedTypes.join(',')
          }
          return axios.get('/api/sandoz/product_matrix', { params })
            .then(response => {
              this.productMasters = response.data.product_masters
              this.salesLines = response.data.sales_lines
              
              // Restore selection after reload
              if (selectedTypes.length > 0) {
                const availableIds = this.salesLines.map(sl => sl.id)
                this.selectedContractTypes = selectedTypes.filter(id => availableIds.includes(id))
              }
            })
            .catch(error => {
              console.error('Error reloading product matrix:', error)
            })
            .finally(() => {
              this.loading = false
            })
        })
        .catch(error => {
          console.error('Error saving changes:', error)
          this.$bvToast.toast(this.$t('core.error_saving_data'), {
            title: this.$t('core.error'),
            variant: 'danger',
            solid: true
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>
